import { Link } from "@tanstack/react-router";
import { LezoWordmark } from "@/components/blocks/brand/LezoLogo";
import { env } from "@/env";
import { ANALYTICS_CONSENT_EVENT } from "@/lib/analytics";
import { CONTACT_EMAIL, CONTACT_URL, QUESTOVERY_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import { SITE_NAME } from "@/lib/site";
import { USE_CASE_KEYS, USE_CASES } from "@/lib/use-cases";

export default function SiteFooter() {
	const showConsentLink =
		env.VITE_ANALYTICS_ENABLED && Boolean(env.VITE_POSTHOG_KEY);
	const { dict, localeParams } = useI18n();
	const footer = dict.footer;
	const nav = dict.nav;

	const handleManageConsent = () => {
		if (typeof window === "undefined") return;
		window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
	};

	const linkClass =
		"text-muted-foreground transition-colors hover:text-foreground";
	const titleClass =
		"font-mono text-[11px] tracking-[0.08em] text-muted-foreground/80 uppercase";

	return (
		<footer className="dark relative isolate overflow-clip border-t bg-background text-foreground">
			<div className="mx-auto grid w-full max-w-7xl gap-12 px-6 pt-20 pb-12 text-sm md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1fr] lg:px-10">
				<div className="space-y-5">
					<Link to="/{-$locale}" params={localeParams} className="inline-flex">
						<LezoWordmark title={SITE_NAME} className="h-6" />
					</Link>
					<p className="max-w-sm text-base text-pretty text-muted-foreground">
						{footer.description}
					</p>
					<a
						href={CONTACT_URL}
						className="inline-flex border-b border-signal/60 pb-0.5 text-base font-medium transition-colors hover:border-signal"
					>
						{CONTACT_EMAIL}
					</a>
				</div>

				<nav aria-label={footer.servicesTitle} className="space-y-4">
					<p className={titleClass}>{footer.servicesTitle}</p>
					<ul className="space-y-2.5">
						<li>
							<Link
								to="/{-$locale}/services/applications-metier"
								params={localeParams}
								className={linkClass}
							>
								{nav.businessApps}
							</Link>
						</li>
						<li>
							<Link
								to="/{-$locale}/services/migration-react-native-expo"
								params={localeParams}
								className={linkClass}
							>
								{nav.expoMigration}
							</Link>
						</li>
					</ul>
				</nav>

				<nav aria-label={nav.useCases} className="space-y-4">
					<p className={titleClass}>{nav.useCases}</p>
					<ul className="space-y-2.5">
						{USE_CASE_KEYS.map((key) => (
							<li key={key}>
								<Link
									to={USE_CASES[key].to}
									params={localeParams}
									className={linkClass}
								>
									{dict.useCases.items[key].card.name}
								</Link>
							</li>
						))}
						<li>
							<Link
								to="/{-$locale}/cas-usage"
								params={localeParams}
								className={linkClass}
							>
								{nav.useCasesAll}
							</Link>
						</li>
					</ul>
				</nav>

				<nav aria-label={footer.companyTitle} className="space-y-4">
					<p className={titleClass}>{footer.companyTitle}</p>
					<ul className="space-y-2.5">
						<li>
							<Link
								to="/{-$locale}/realisations"
								params={localeParams}
								className={linkClass}
							>
								{nav.work}
							</Link>
						</li>
						<li>
							<Link
								to="/{-$locale}/produits"
								params={localeParams}
								className={linkClass}
							>
								{nav.products}
							</Link>
						</li>
						<li>
							<Link
								to="/{-$locale}/a-propos"
								params={localeParams}
								className={linkClass}
							>
								{nav.about}
							</Link>
						</li>
						<li>
							<Link
								to="/{-$locale}/contact"
								params={localeParams}
								className={linkClass}
							>
								{footer.contact}
							</Link>
						</li>
						<li>
							<a
								href={QUESTOVERY_URL}
								target="_blank"
								rel="noopener"
								className={linkClass}
							>
								Questovery
							</a>
						</li>
					</ul>
				</nav>

				<nav aria-label={footer.infoTitle} className="space-y-4">
					<p className={titleClass}>{footer.infoTitle}</p>
					<ul className="space-y-2.5">
						<li>
							<Link
								to="/{-$locale}/docs/$slug"
								params={{ ...localeParams, slug: "legal" }}
								className={linkClass}
							>
								{footer.legal}
							</Link>
						</li>
						<li>
							<Link
								to="/{-$locale}/docs/$slug"
								params={{ ...localeParams, slug: "privacy" }}
								className={linkClass}
							>
								{footer.privacy}
							</Link>
						</li>
						{showConsentLink ? (
							<li>
								<button
									type="button"
									onClick={handleManageConsent}
									className={linkClass}
								>
									{footer.cookies}
								</button>
							</li>
						) : null}
					</ul>
				</nav>
			</div>
			<div
				aria-hidden="true"
				className="pointer-events-none mx-auto w-full max-w-7xl px-6 pt-8 text-foreground/[0.06] select-none lg:px-10 [&_.lz-cursor]:fill-signal/25"
			>
				<LezoWordmark title="" className="h-auto w-full" />
			</div>
			<div className="relative border-t">
				<p className="mx-auto w-full max-w-7xl px-6 py-6 font-mono text-[11px] tracking-[0.08em] text-muted-foreground lg:px-10">
					© {new Date().getFullYear()} {SITE_NAME}. {footer.rights}
				</p>
			</div>
		</footer>
	);
}
